"use client";

import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { RootState, useAppSelector } from "@/lib/redux/root";
import { useBrand } from "@/lib/hooks/useBrand";

const buttonVariants = cva(
	"inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all duration-150 transform-gpu focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-ring focus-visible:ring-offset-4 focus-visible:shadow-xl disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
	{
		variants: {
			variant: {
				default: "text-white shadow",
				destructive:
					"bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90",
				outline:
					"border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground",
				secondary:
					"bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80",
				ghost: "hover:bg-accent hover:text-accent-foreground",
				link: "underline-offset-4 hover:underline",
			},
			size: {
				default: "h-9 px-4 py-2",
				sm: "h-8 rounded-md px-3 text-xs",
				lg: "h-10 rounded-md px-8",
				icon: "h-9 w-9",
			},
		},
		defaultVariants: {
			variant: "default",
			size: "default",
		},
	}
);

export interface BrandButtonProps
	extends
		React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	asChild?: boolean;
}

const BrandButton = React.forwardRef<HTMLButtonElement, BrandButtonProps>(
	({ className, variant, size, asChild = false, style, ...props }, ref) => {
		const {
			loading,
			location: { appInfo },
		} = useAppSelector((state: RootState) => state.location);

		const Comp = asChild ? Slot : "button";
		const { brandConfig } = useBrand();
		// Apply brand colors only for default variant
		const brandStyle =
			variant === "default" || !variant
				? {
						backgroundColor: appInfo?.primaryColor || brandConfig.primaryColor,
						...style,
					}
				: style;

		const handleMouseEnter = (e: React.MouseEvent<HTMLButtonElement>) => {
			if (variant === "default" || !variant) {
				e.currentTarget.style.backgroundColor =
					appInfo?.hoverColor || brandConfig.hoverColor;
			}
			props.onMouseEnter?.(e as any);
		};

		const handleMouseLeave = (e: React.MouseEvent<HTMLButtonElement>) => {
			// If focused, keep hover color; otherwise revert to primary
			if (variant === "default" || !variant) {
				if (e.currentTarget.matches(":focus")) {
					e.currentTarget.style.backgroundColor =
						appInfo?.hoverColor || brandConfig.hoverColor;
				} else {
					e.currentTarget.style.backgroundColor =
						appInfo?.primaryColor || brandConfig.primaryColor;
				}
			}
			props.onMouseLeave?.(e as any);
		};

		const handleFocus = (e: React.FocusEvent<HTMLButtonElement>) => {
			if (variant === "default" || !variant) {
				e.currentTarget.style.backgroundColor =
					appInfo?.hoverColor || brandConfig.hoverColor;
			}
			props.onFocus?.(e as any);
		};

		const handleBlur = (e: React.FocusEvent<HTMLButtonElement>) => {
			if (variant === "default" || !variant) {
				if (e.currentTarget.matches(":hover")) {
					e.currentTarget.style.backgroundColor =
						appInfo?.hoverColor || brandConfig.hoverColor;
				} else {
					e.currentTarget.style.backgroundColor =
						appInfo?.primaryColor || brandConfig.primaryColor;
				}
			}
			props.onBlur?.(e as any);
		};

		return (
			<Comp
				className={cn(buttonVariants({ variant, size, className }))}
				ref={ref}
				style={brandStyle}
				onMouseEnter={handleMouseEnter}
				onMouseLeave={handleMouseLeave}
				onFocus={handleFocus}
				onBlur={handleBlur}
				tabIndex={props.tabIndex ?? 0}
				{...props}
			/>
		);
	}
);
BrandButton.displayName = "BrandButton";

export { BrandButton, buttonVariants };
